import React, { memo, useCallback } from 'react';
import { formatTimestamp as formatCompactTimestamp } from '../../utils/formatTimestamp';
import styles from './CommentList.module.scss';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog/ConfirmDeleteDialog';
import TrashCan from '../Icons/TrashCan';
import { useUser } from '../../config/UserContext';
import { useSongs } from '../../config/SongContext';
import CommentForm from '../CommentForm/CommentForm';
import { useNavigation } from '../../config/NavigationContext';
import { getCommentRecordId, getReplyRecordId } from '../../utils/commentIds';
import { useCommentListAvatars } from './hooks/useCommentListAvatars';
import { useCommentDeleteDialog } from './hooks/useCommentDeleteDialog';
import { useCommentLikeActions } from './hooks/useCommentLikeActions';
import { useCommentReplyUi } from './hooks/useCommentReplyUi';
import { CommentAuthorCluster } from './components/CommentAuthorCluster';
import { CommentMessageContent } from './components/CommentMessageContent';

const CommentList = memo(
  ({
    comments,
    currentUser,
    onDelete,
    onDeleteReply,
    onAddReply,
    onLikeComment,
    onUnlikeComment,
    onLikeReply,
    onUnlikeReply,
    songId,
    handleUserPageNavFromSong,
    highlightCommentId,
    highlightReplyId,
  }) => {
    const songsContext = useSongs();
    const likeComment = onLikeComment || songsContext.likeComment;
    const unlikeComment = onUnlikeComment || songsContext.unlikeComment;
    const likeReply = onLikeReply || songsContext.likeReply;
    const unlikeReply = onUnlikeReply || songsContext.unlikeReply;

    const { getUserDetailsById } = useUser();
    const { handleUserPageNavByHandle } = useNavigation();
    const { userAvatars, defaultAvatar } = useCommentListAvatars(
      comments,
      getUserDetailsById
    );

    const { handleLike, handleUnlike } = useCommentLikeActions({
      likeComment,
      unlikeComment,
      likeReply,
      unlikeReply,
    });

    const {
      showConfirmDialog,
      selectedReplyIndex,
      handleOpenConfirmDialog,
      handleCloseDialog,
      handleConfirmDelete,
    } = useCommentDeleteDialog({ onDelete, onDeleteReply });

    const { replyInputVisible, toggleReplyInput, handleAddReply } =
      useCommentReplyUi(onAddReply);

    const handleMentionClick = useCallback(
      (e) => {
        const el = e.target.closest('[data-mention-handle]');
        if (el) {
          e.preventDefault();
          handleUserPageNavByHandle(el.dataset.mentionHandle);
        }
      },
      [handleUserPageNavByHandle]
    );

    const formatTimestamp = useCallback((timestamp) => {
      return formatCompactTimestamp(timestamp) || 'Unknown time';
    }, []);

    if (!Array.isArray(comments) || comments.length === 0) {
      return null;
    }

    return (
      <div className={styles.comments}>
        Comments:
        <div className={styles.commentList}>
          {comments?.map((comment, commentIndex) => {
            const commentId = getCommentRecordId(comment);
            const commentKey = commentId || `comment-${commentIndex}`;
            const commentMenuOptions = [
              {
                label: 'Delete',
                handler: () =>
                  handleOpenConfirmDialog(commentId, null, commentIndex),
                style: { color: '#EF4444' },
                icon: <TrashCan />,
                isDestructive: true,
              },
            ];
            const isCommentHighlighted =
              commentId != null &&
              highlightCommentId != null &&
              String(commentId) === String(highlightCommentId);

            return (
              <div
                className={`${styles.container} ${isCommentHighlighted ? styles.highlighted : ''}`}
                key={commentKey}
                id={commentId ? `comment-${commentId}` : undefined}
              >
                <div className={styles.mainComment}>
                  <CommentAuthorCluster
                    userId={comment.userId}
                    userAvatars={userAvatars}
                    defaultAvatar={defaultAvatar}
                    currentUser={currentUser}
                    menuOptions={commentMenuOptions}
                    timestamp={comment.timestamp}
                    formatTimestamp={formatTimestamp}
                    handleUserPageNavFromSong={handleUserPageNavFromSong}
                  />
                  <div className={styles.commentBody}>
                    <CommentMessageContent
                      variant="comment"
                      rawText={comment.commentText || comment.text || ''}
                      mentionedHandles={comment.mentionedHandles}
                      songId={songId}
                      comment={comment}
                      commentIndex={commentIndex}
                      currentUser={currentUser}
                      handleLike={handleLike}
                      handleUnlike={handleUnlike}
                      onMentionClick={handleMentionClick}
                    />
                  </div>
                </div>

                {comment.replies && comment.replies.length > 0 && (
                  <div className={styles.replies}>
                    {comment.replies.map((reply, replyIndex) => {
                      const replyId = getReplyRecordId(reply);
                      const replyMenuOptions = [
                        {
                          label: 'Delete',
                          handler: () =>
                            handleOpenConfirmDialog(
                              commentId,
                              replyIndex,
                              commentIndex
                            ),
                          style: { color: '#EF4444' },
                          icon: <TrashCan />,
                          isDestructive: true,
                        },
                      ];
                      const isReplyHighlighted =
                        replyId != null &&
                        highlightReplyId != null &&
                        String(replyId) === String(highlightReplyId);

                      return (
                        <div
                          className={`${styles.reply} ${isReplyHighlighted ? styles.highlighted : ''}`}
                          key={replyId || `reply-${replyIndex}`}
                          id={replyId ? `reply-${replyId}` : undefined}
                        >
                          <CommentAuthorCluster
                            userId={reply.userId}
                            userAvatars={userAvatars}
                            defaultAvatar={defaultAvatar}
                            currentUser={currentUser}
                            menuOptions={replyMenuOptions}
                            timestamp={reply.timestamp}
                            formatTimestamp={formatTimestamp}
                            handleUserPageNavFromSong={handleUserPageNavFromSong}
                          />
                          <div className={styles.replyBody}>
                            <CommentMessageContent
                              variant="reply"
                              rawText={reply.commentText || reply.text || ''}
                              mentionedHandles={reply.mentionedHandles}
                              songId={songId}
                              comment={comment}
                              reply={reply}
                              commentIndex={commentIndex}
                              replyIndex={replyIndex}
                              currentUser={currentUser}
                              handleLike={handleLike}
                              handleUnlike={handleUnlike}
                              onMentionClick={handleMentionClick}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {currentUser && (
                  <>
                    {replyInputVisible[commentKey] ? (
                      <CommentForm
                        currentUser={currentUser}
                        addComment={(_, newReply) =>
                          handleAddReply(songId, commentId, commentKey, newReply, commentIndex)
                        }
                        songId={songId}
                        placeholder="Leave a reply"
                      />
                    ) : (
                      <div className={styles.leaveReplyButtonContainer}>
                        <button
                          className={styles.leaveReplyButton}
                          onClick={() => toggleReplyInput(commentKey)}
                        >
                          Leave Reply
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
          {showConfirmDialog && (
            <ConfirmDeleteDialog
              show={showConfirmDialog}
              message={
                selectedReplyIndex !== null
                  ? 'Are you sure you want to delete your reply?'
                  : 'Are you sure you want to delete your comment?'
              }
              onClose={handleCloseDialog}
              confirm={handleConfirmDelete}
            />
          )}
        </div>
      </div>
    );
  }
);

export default CommentList;
